import React, { useCallback, useEffect, useState } from "react";
import * as api from "./csaApi";
import CsaReview from "./CsaReview";

/**
 * Finish and submit.
 *
 * Submit materialises the 163 observations and freezes the scoring snapshot
 * (§4, §5), so it is the one action that cannot happen against a local copy.
 * Two gates, in this order:
 *
 *   1. Everything on this device has reached the server. Submitting with work
 *      still queued would freeze a snapshot that is missing answers the auditor
 *      has already given — and the snapshot is what the client's report says.
 *   2. The server's own readiness rules: every question answered, every gap
 *      written up, every photo assigned.
 *
 * Unused notes warn rather than block, because a note may have been a reminder
 * to self that was never a finding (§7e). Photos block, because an unattached
 * photo is evidence that belongs somewhere.
 */
export default function CsaSubmit({ auditId, audit, canReview, canReopen, onSubmitted, onChanged }) {
  const [readiness, setReadiness] = useState(null);
  const [sync, setSync] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  useEffect(() => api.subscribeSync(setSync), []);

  const check = useCallback(() => {
    setError(null);
    api
      .getReadiness(auditId)
      .then(setReadiness)
      .catch((e) => setError(e.message));
  }, [auditId]);

  useEffect(check, [check]);

  const syncFirst = async () => {
    setBusy(true);
    try {
      await api.flush(auditId);
      await api.resync(auditId);
      check();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.submitAudit(auditId, {});
      setDone(r);
      if (onSubmitted) onSubmitted(r);
    } catch (e) {
      setError(e.message);
      // A refusal usually means readiness moved under us — show why, now.
      check();
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="cap-writeup">
        <div className="wu-empty">
          <h2>Submitted</h2>
          <p>
            {done.overall_pct == null
              ? "This audit has been submitted."
              : `Overall conformance ${done.overall_pct}% — ${done.maturity_band}.`}
            {done.assessed
              ? ` ${done.assessed} questions counted towards the score.`
              : ""}
          </p>
        </div>
      </div>
    );
  }

  /*
   * Once submitted, this tab becomes the review step rather than a dead end.
   * It used to say "Already submitted … can no longer be edited", which was
   * true and useless: the endpoints to sign off or return it existed the whole
   * time and nothing offered them.
   */
  if (audit && audit.status !== "draft") {
    return (
      <CsaReview
        auditId={auditId}
        audit={audit}
        canReview={canReview}
        canReopen={canReopen}
        onChanged={onChanged}
      />
    );
  }

  const offline = sync && !sync.online;
  const unsynced = sync ? sync.pending : 0;
  /*
   * Photographs waiting to upload block submit as firmly as queued answers do.
   *
   * They used not to: they are not outbox entries, so `pending` did not see
   * them, and the sync that uploads them catches its own failures and returns
   * null. A network drop at the wrong moment therefore left an audit that
   * reported itself fully synced while its evidence was still on the device —
   * and submitting freezes the projection, so those photographs were missing
   * from the report for good.
   */
  const photosWaiting = sync ? sync.photos_pending || 0 : 0;
  const blockedBySync = offline || unsynced > 0 || photosWaiting > 0;
  const canSubmit = readiness && readiness.ready && !blockedBySync && !busy;

  return (
    <div className="cap-writeup">
      <div className="wu-head">
        <div>
          <h2>Finish and submit</h2>
          {/*
            This used to end "after that the audit can no longer be edited",
            which was true until the auditor could reopen it themselves (§5g).
            Left as it was, it would have talked people out of submitting —
            which is the opposite of what a Finish screen is for.
          */}
          <p>
            Submitting writes the observations and fixes the score. You can reopen
            it afterwards if you need to change something.
          </p>
        </div>
        <button type="button" className="small-btn" onClick={check} disabled={busy}>
          Re-check
        </button>
      </div>

      {error ? <p className="wu-error">{error}</p> : null}

      {/* gate 1 */}
      <div className={"submit-gate " + (blockedBySync ? "blocked" : "clear")}>
        <div>
          <strong>{blockedBySync ? "Not everything has synced" : "Everything has synced"}</strong>
          <span>
            {offline
              ? "You are offline. Your work is safe on this device — connect to a network to submit."
              : unsynced
              ? `${unsynced} change${unsynced === 1 ? "" : "s"} on this device have not reached the server yet.`
              : photosWaiting
              /*
               * Named separately from the change count. "3 changes have not
               * reached the server" sends an auditor looking for answers they
               * have not saved; the thing actually waiting is a photograph,
               * and pressing Sync is what fixes it.
               */
              ? `${photosWaiting} photo${photosWaiting === 1 ? "" : "s"} ` +
                `${photosWaiting === 1 ? "is" : "are"} still on this device. ` +
                "Submitting now would leave them out of the report for good."
              : "Every change on this device is on the server."}
          </span>
        </div>
        {(unsynced || photosWaiting) && !offline ? (
          <button type="button" className="small-btn" onClick={syncFirst} disabled={busy}>
            {busy ? "Syncing…" : "Sync now"}
          </button>
        ) : null}
      </div>

      {/* gate 2 */}
      {!readiness ? (
        <div className="cap-loading">Checking…</div>
      ) : (
        <>
          <div className={"submit-gate " + (readiness.ready ? "clear" : "blocked")}>
            <div>
              <strong>{readiness.ready ? "Ready to submit" : "Not ready yet"}</strong>
              <span>
                {readiness.ready
                  ? "Every question is answered and every gap is written up."
                  : "These need attention first."}
              </span>
            </div>
          </div>

          {readiness.problems && readiness.problems.length ? (
            <ul className="submit-list problems">
              {readiness.problems.map((p, i) => (
                <li key={p.rule + i}>
                  <strong>{p.rule}</strong>
                  <span>{p.message || describe(p)}</span>
                </li>
              ))}
            </ul>
          ) : null}

          {readiness.warnings && readiness.warnings.length ? (
            <ul className="submit-list warnings">
              {readiness.warnings.map((w, i) => (
                <li key={w.rule + i}>
                  <strong>{w.rule}</strong>
                  <span>{w.message || describe(w)}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}

      <div className="scope-actions">
        <button type="button" className="cap-btn" disabled={!canSubmit} onClick={submit}>
          {busy ? "Submitting…" : "Submit this audit"}
        </button>
      </div>
    </div>
  );
}

/**
 * The server names a rule; turn it into something an auditor can act on.
 *
 * The message the server sends is already readable, so this only fills in when
 * one is missing — a rule with no message should still not render as a blank.
 */
const RULE_TEXT = {
  unanswered: "Some questions have no answer yet.",
  missing_note: "Every No and Partial needs a note — the write-up screen collects them.",
  unassigned_photos:
    "Every photo must be attached to a question or discarded. The Photos tab lists them.",
  unconfirmed_carry_forward: "Some carried-over answers have not been confirmed.",
  unconfirmed_exclusion: "Some scope exclusions have not been confirmed.",
  unused_notes: "Some notes were never used — check none of them was a finding.",
};

function describe(p) {
  if (RULE_TEXT[p.rule]) return RULE_TEXT[p.rule];
  if (p.count != null) return `${p.count} item${p.count === 1 ? "" : "s"}`;
  return "";
}
